import JSZip from "jszip"
import { ref, uploadBytes } from "firebase/storage"

import { storage } from "@/config/firebase"

const MONTH_FOLDERS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const MAX_REPORT_ARCHIVE_BYTES = 25 * 1024 * 1024
type ReportPlatform = "amazon" | "meesho"

interface ReportSourceFile {
  file: File
  role: string
}

interface ReportArchiveUpload {
  monthFolder: string
  reportYear: string
  storagePath: string
}

const sanitizeArchiveSegment = (value: string): string => {
  const sanitized = value.trim().replace(/[^a-zA-Z0-9._-]+/g, "-")
  return sanitized.replace(/^-+|-+$/g, "") || "file"
}

const parseMonthKey = (monthKey: string): { monthFolder: string; year: string } => {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(monthKey)
  if (!match) throw new Error("The report month is invalid.")

  return {
    year: match[1],
    monthFolder: MONTH_FOLDERS[Number(match[2]) - 1]
  }
}

const buildReportFilesStoragePath = (uid: string, platform: ReportPlatform, monthKey: string): string => {
  if (!uid || uid.includes("/")) throw new Error("The report owner is invalid.")
  const { monthFolder, year } = parseMonthKey(monthKey)
  return `${uid}/reports/${year}/${monthFolder}/${platform}/reportfiles.zip`
}

const createReportFilesArchive = async (sourceFiles: ReportSourceFile[]): Promise<Uint8Array> => {
  if (sourceFiles.length === 0) throw new Error("Select the report files before generating the report.")

  const archive = new JSZip()
  sourceFiles.forEach(({ file, role }, index) => {
    const archiveName = `${String(index + 1).padStart(2, "0")}-${sanitizeArchiveSegment(role)}-${sanitizeArchiveSegment(file.name)}`
    archive.file(archiveName, file)
  })

  const bytes = await archive.generateAsync({
    type: "uint8array",
    compression: "DEFLATE",
    compressionOptions: { level: 6 }
  })
  if (bytes.byteLength > MAX_REPORT_ARCHIVE_BYTES) {
    throw new Error("The compressed report files exceed the 25 MB storage limit.")
  }
  return bytes
}

const uploadReportFilesArchive = async (
  uid: string,
  platform: ReportPlatform,
  monthKey: string,
  sourceFiles: ReportSourceFile[]
): Promise<ReportArchiveUpload> => {
  const storagePath = buildReportFilesStoragePath(uid, platform, monthKey)
  const { monthFolder, year: reportYear } = parseMonthKey(monthKey)
  const archiveBytes = await createReportFilesArchive(sourceFiles)
  await uploadBytes(ref(storage, storagePath), archiveBytes, {
    contentType: "application/zip",
    cacheControl: "private, no-store, max-age=0",
    customMetadata: { monthKey, platform }
  })
  return { monthFolder, reportYear, storagePath }
}

export { MAX_REPORT_ARCHIVE_BYTES, buildReportFilesStoragePath, createReportFilesArchive, uploadReportFilesArchive }
export type { ReportSourceFile }
